/**
 * ConsequencePanel Component - Phase 2C-2
 * 
 * CRITICAL BSL COMPLIANCE:
 * - Shows factual outcomes ONLY
 * - No "risk level" or "danger" indicators
 * - Labels: "If correct: +X" / "If incorrect: -X"
 * - Neutral styling throughout
 */

import { Card, CardContent } from '../common';
import { Currency } from '../common';

interface ConsequencePanelProps {
  stakeAmount: number;
  odds?: number;
  currentBalance: number;
  className?: string;
}

export function ConsequencePanel({
  stakeAmount,
  odds,
  currentBalance,
  className = '',
}: ConsequencePanelProps) {
  // Calculate potential outcomes
  const potentialWin = calculatePotentialWin(stakeAmount, odds);
  const balanceIfCorrect = currentBalance - stakeAmount + potentialWin;
  const balanceIfIncorrect = currentBalance - stakeAmount;

  return (
    <Card className={`bg-slate-50 border-slate-200 ${className}`}>
      <CardContent className="p-4">
        <div className="text-sm font-medium text-slate-700 mb-3">
          Potential Outcomes
        </div>
        
        <div className="space-y-3">
          {/* If Correct */}
          <div className="flex justify-between items-center p-3 bg-white rounded border border-slate-200">
            <div>
              <div className="text-sm text-slate-600">If correct</div>
              <div className="text-xs text-slate-500">Your prediction wins</div>
            </div>
            <div className="text-right">
              <div className="font-mono font-medium text-slate-900">
                +<Currency value={potentialWin - stakeAmount} />
              </div>
              <div className="text-xs text-slate-500 font-mono">
                Balance: <Currency value={balanceIfCorrect} />
              </div>
            </div>
          </div>

          {/* If Incorrect */}
          <div className="flex justify-between items-center p-3 bg-white rounded border border-slate-200">
            <div>
              <div className="text-sm text-slate-600">If incorrect</div>
              <div className="text-xs text-slate-500">Your prediction loses</div>
            </div>
            <div className="text-right">
              <div className="font-mono font-medium text-slate-900">
                -<Currency value={stakeAmount} />
              </div>
              <div className="text-xs text-slate-500 font-mono">
                Balance: <Currency value={balanceIfIncorrect} />
              </div>
            </div>
          </div>
        </div>

        {/* Stake Summary - Factual Only */}
        <div className="mt-4 pt-3 border-t border-slate-200 text-sm">
          <div className="flex justify-between text-slate-600">
            <span>Stake Amount</span>
            <span className="font-mono"><Currency value={stakeAmount} /></span>
          </div>
          {odds && (
            <div className="flex justify-between text-slate-600 mt-1">
              <span>Odds</span>
              <span className="font-mono">{odds > 0 ? '+' : ''}{odds}</span>
            </div>
          )}
          <div className="flex justify-between text-slate-600 mt-1">
            <span>Potential Return</span>
            <span className="font-mono"><Currency value={potentialWin} /></span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Calculate potential win based on American odds
 */
function calculatePotentialWin(stake: number, odds?: number): number {
  if (!odds) {
    // No odds provided, assume even money (1:1)
    return stake * 2;
  }

  if (odds > 0) {
    // Positive odds: profit per $100 wagered
    return stake + (stake * odds / 100);
  } else {
    // Negative odds: amount needed to win $100
    return stake + (stake * 100 / Math.abs(odds));
  }
}
